package com.royceecoleman.bill.shared.billing

import kotlinx.serialization.Serializable
import kotlin.math.abs

/** The lawyer's standing rules: one rule set per account, applied to every draft for every client. */
@Serializable
data class BillingRules(
    val text: String = "",
    /** 0 until the first save; each saved version takes the next revision. */
    val revision: Int = 0,
    val updatedAt: Long = 0,
    /** Newest first, without the text of each version. */
    val history: List<RulesVersion> = emptyList(),
    val chatsRemaining: Int = 0,
) {
    companion object {
        const val MAX_LENGTH = 100_000
        const val MAX_MESSAGE = 2_000

        /** [MAX_LENGTH] as shown in messages: 100,000. */
        val MAX_LENGTH_TEXT = thousands(MAX_LENGTH)

        /** A count with comma thousands separators, such as 12,345, the same on every platform. */
        fun thousands(count: Int): String {
            val digits = abs(count.toLong()).toString()
            return (if (count < 0) "-" else "") + digits.reversed().chunked(3).joinToString(",").reversed()
        }
    }
}

@Serializable
data class RulesVersion(
    val revision: Int,
    val createdAt: Long,
    /** USER, AGENT, UPLOAD, UNDO or RESTORE. */
    val source: String,
    val summary: String = "",
)

@Serializable
data class RulesEdit(
    val revision: Int,
    val text: String,
)

@Serializable
data class RulesChatTurn(
    /** "user" or "assistant". */
    val role: String,
    val content: String,
)

@Serializable
data class RulesChatRequest(
    val revision: Int,
    val message: String,
    /** The last few turns of this page's conversation, kept only in the browser. */
    val conversation: List<RulesChatTurn> = emptyList(),
)

/** A PDF or Word (.docx) document whose text replaces the rules. The file itself is not kept. */
@Serializable
data class RulesUpload(
    val revision: Int,
    val filename: String,
    val contentBase64: String,
)

@Serializable
data class RulesRestoreRequest(
    val revision: Int,
    val versionRevision: Int,
)

/** The rules after a save, chat, upload, undo or restore. [previousText] is the text before the change, for a diff. */
@Serializable
data class RulesUpdate(
    val rules: BillingRules,
    val changed: Boolean = false,
    val previousText: String = "",
    val summary: String = "",
    /** BILL's answer to a chat message; empty for other changes. */
    val reply: String = "",
)
